import { InfoCircleIcon, LightbulbIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import type * as React from 'react';
import { Separator } from '../separator';
import { Text, TextColor, TextSize, TextVariant, TextWeight } from '../text';

/**
 * Metadata for the info popup displayed alongside the caption.
 */
interface InfoMetaData {
  /** The title text displayed at the top of the popup. */
  title?: string;
  /**
   * The description content displayed below the title.
   * Should not contain interactive elements (links, buttons)
   * as the popup is not keyboard-accessible for nested interactions.
   */
  description: React.ReactNode;
}

interface CaptionProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Content to wrap with this caption.
   * Rendered above the caption with configurable gap.
   */
  children: React.ReactNode;
  /**
   * The caption text to display.
   */
  label: string;
  /**
   * Optional metadata for the info popup.
   * If provided, renders an info icon that shows a popup on hover with title and description.
   * Ignored if `infoCustomComponent` is provided.
   */
  infoMetaData?: InfoMetaData;
  /**
   * Custom component to render instead of the default info popup.
   * Takes priority over `infoMetaData`.
   */
  infoCustomComponent?: React.ReactNode;
  /**
   * Optional content to render after the label and info icon, inline with them.
   */
  trailingContent?: React.ReactNode;
  /**
   * Gap between children and caption.
   * @default 'gap-12'
   */
  gap?: string;
  /**
   * Whether to show the top separator line.
   * @default true
   */
  showSeparator?: boolean;
}

const containerClasses = cva({
  base: 'flex flex-col gap-2 w-full',
});

const contentClasses = cva({
  base: 'flex items-center justify-between gap-2',
});

const iconWrapperClasses = cva({
  base: [
    'relative cursor-pointer rounded-xxs outline-none',
    'focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-1',
    'text-quaternary hover:text-quaternary-hover transition-colors',
    'group/info',
  ],
});

const infoPopupBaseClasses = cva({
  base: [
    'absolute left-0 top-full pt-2',
    'opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible',
    'group-focus-visible/info:opacity-100 group-focus-visible/info:visible',
    'transition-opacity duration-150 delay-100',
    'z-10',
  ],
});

const infoPopupClasses = cva({
  base: [infoPopupBaseClasses(), 'min-w-[215px] p-8 shadow-lg', 'bg-primary', 'flex flex-col gap-4 text-left'],
});

const InfoPopup = ({ title, description }: InfoMetaData) => (
  <div className={infoPopupClasses()}>
    <div className="w-14 h-14 flex items-center justify-center border border-primary p-2">
      <LightbulbIcon size={16} className="text-tertiary" />
    </div>
    {title && (
      <Text size={TextSize.Sm} weight={TextWeight.Semibold} color={TextColor.Primary} elementType="div">
        {title}
      </Text>
    )}
    <Text
      size={TextSize.Sm}
      weight={TextWeight.Regular}
      color={TextColor.Secondary}
      showEllipsis={false}
      elementType="div"
    >
      {description}
    </Text>
  </div>
);

/**
 * Caption component displays a compact caption with a top rule, small text,
 * and an optional info icon that shows a popup on hover/focus.
 * Wraps children components with caption displayed below.
 *
 * @example
 * // Basic usage
 * <Caption label="Hero">
 *   <ProductImage />
 * </Caption>
 *
 * @example
 * // With custom gap
 * <Caption label="Hero" gap="gap-8">
 *   <ProductImage />
 * </Caption>
 */
const Caption = (props: CaptionProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const {
    children,
    label,
    className,
    infoMetaData,
    infoCustomComponent,
    trailingContent,
    gap = 'gap-12',
    showSeparator = true,
    ref,
    ...other
  } = props;

  // Determine which info component to render (custom takes priority)
  const hasInfoPopup = infoCustomComponent || infoMetaData;

  const captionContent = (
    <div className={cx(containerClasses())}>
      {showSeparator && <Separator />}
      <div className={contentClasses()}>
        {hasInfoPopup ? (
          <button type="button" className={cx(contentClasses(), iconWrapperClasses())} aria-label="More information">
            <Text
              variant={TextVariant.MonoXs}
              color={TextColor.Quaternary}
              showEllipsis={false}
              elementType="span"
              className="leading-none uppercase"
            >
              {label}
            </Text>
            <InfoCircleIcon
              className="text-gray-utility-400 group-hover/info:text-gray-utility-500 transition-colors"
              size={12}
            />
            {infoCustomComponent ? (
              <div className={infoPopupBaseClasses()}>{infoCustomComponent}</div>
            ) : (
              <InfoPopup {...(infoMetaData as InfoMetaData)} />
            )}
          </button>
        ) : (
          <Text
            variant={TextVariant.MonoXs}
            color={TextColor.Quaternary}
            showEllipsis={false}
            elementType="span"
            className="leading-none uppercase"
          >
            {label}
          </Text>
        )}
        {trailingContent}
      </div>
    </div>
  );

  return (
    <div ref={ref} className={cx('flex flex-col', gap, className)} {...other}>
      {captionContent}
      {children}
    </div>
  );
};

export type { CaptionProps, InfoMetaData };
export { Caption };
